/* eslint-disable valid-jsdoc */
/* eslint-disable max-len */
/* eslint-disable no-undefined */

import { VersionRange } from '../models/core/version-range.js';
import { SerializedData } from '../models/dto/dto.js';
import { JSONUtils } from './json-utils.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';
import { SerializationException } from '../exceptions/serialization-exception.js';

export class MapUtils {
  static has(map: SerializedData, key: string): boolean {
    return map[key] != undefined;
  }

  static replace<T, R>(map: SerializedData, key: string, expectedValue: T, desiredValue: R): void {
    if(map[key] == expectedValue) {
      map[key] = desiredValue;
    }
  }

  /**
   * @deprecated Use the non generic methods that best fit the required type.
   */
static get<T>(map: SerializedData, key: string, defValue?: T): T {
    const value = map[key];

    if(isNotNullOrUndefined(value)) {
      return value as T;
    }

    if(isNotNullOrUndefined(defValue)) {
      return defValue;
    }

    throw new SerializationException(`Missing or null field with key {${key}} in data map: ${JSON.stringify(map)}`);
  }

  static getString(map: SerializedData, key: string, defValue?: string): string {
    const value = this.get<string>(map, key, defValue);

    return JSONUtils.decodeString(value);
  }

  /**
   * @deprecated Use {@link MapUtils.getInteger} or {@link MapUtils.getDouble} instead.
   */
  static getNumber(map: SerializedData, key: string, defValue?: number): number {
    const value = this.get<number>(map, key, defValue);

    return JSONUtils.decodeNumber(value);
  }

  static getInteger(map: SerializedData, key: string, defValue?: number): number {
    const value = this.get<number>(map, key, defValue);

    return JSONUtils.decodeInteger(value);
  }

  static getDouble(map: SerializedData, key: string, defValue?: number): number {
    const value = this.get<number>(map, key, defValue);

    return JSONUtils.decodeDouble(value);
  }

  static getBoolean(map: SerializedData, key: string, defValue?: boolean): boolean {
    const value = this.get<boolean>(map, key, defValue);

    return JSONUtils.decodeBoolean(value);
  }

/**
 * @deprecated Use the non generic methods that best fit the required type.
 */
static getOrNull<T>(map: SerializedData, key: string): T | null {
    const value = map[key];
    if(value !== undefined) {
      if(value === null) return null;
      return value as T;
    }

    return null;
  }

  /**
   * @deprecated Use the non generic methods that best fit the required type.
   */
  static getOrNullOrUndefined<T>(map: SerializedData, key: string): T | null | undefined {
    const value = map[key];
    
    if(value === undefined) return undefined;
    if(value === null) return null;
    
    return value as T;
  }

  static getStringOrNull(map: SerializedData, key: string): string | null {
    const value = this.getOrNull<string>(map, key);

    return JSONUtils.decodeStringOrNull(value);
  }

  static getStringOrNullOrUndefined(map: SerializedData, key: string): string | null | undefined {
    const value = this.getOrNullOrUndefined<string>(map, key);

    return JSONUtils.decodeStringOrNullOrUndefined(value);
  }

  /**
   * @deprecated Use {@link MapUtils.getIntegerOrNull} or {@link MapUtils.getDoubleOrNull} instead.
   */
  static getNumberOrNull(map: SerializedData, key: string): number | null {
    const value = this.getOrNull<number>(map, key);

    return JSONUtils.decodeNumberOrNull(value);
  }

  static getIntegerOrNull(map: SerializedData, key: string): number | null {
    const value = this.getOrNull<number>(map, key);

    return JSONUtils.decodeIntegerOrNull(value);
  }

  static getDoubleOrNull(map: SerializedData, key: string): number | null {
    const value = this.getOrNull<number>(map, key);

    return JSONUtils.decodeDoubleOrNull(value);
  }

  static getBooleanOrNull(map: SerializedData, key: string): boolean | null {
    const value = this.getOrNull<boolean>(map, key);

    return JSONUtils.decodeBooleanOrNull(value);
  }

  static getBooleanOrNullOrUndefined(map: SerializedData, key: string): boolean | null | undefined {
    const value = this.getOrNullOrUndefined<boolean>(map, key);

    return JSONUtils.decodeBooleanOrNullOrUndefined(value);
  }

  static getRecordOrNull(map: SerializedData, key: string): SerializedData | null {
    const value = this.getOrNull(map, key);

    return JSONUtils.decodeRecordOrNull(value);
  }

  static getRecordOrNullOrUndefined(map: SerializedData, key: string): SerializedData | null | undefined {
    const value = this.getOrNullOrUndefined(map, key);

    return JSONUtils.decodeRecordOrNullOrUndefined(value);
  }

  static getRecord(map: SerializedData, key: string): SerializedData {
    const value = this.get(map, key);

    return JSONUtils.decodeRecord(value);
  }

  static getRaw(map: SerializedData, key: string, defValue?: unknown): unknown {
    const value = map[key];

    // eslint-disable-next-line no-undefined
    if(value !== undefined) {
      return value;
    }

    return defValue ?? null;
  }

  static getMap<K, V>(
      map: SerializedData,
      key: string,
      mapFunc: (k: string, v: unknown) => {key: K, value: V},
  ): Map<K, V> {
    const value = map[key];
    const result: Map<K, V> = new Map<K, V>();

    if(isNotNullOrUndefined(value) && value instanceof Object) {
      for(const [k, v] of Object.entries(value)) {
        const entry = mapFunc(k, v);
        result.set(entry.key, entry.value);
      }

      return result;
    }

    throw new SerializationException(`Missing field with key: ${key} in data map: ${JSON.stringify(map)}`);
  }

  static getMapOrNull<K, V>(
      map: SerializedData,
      key: string,
      mapFunc: (k: string, v: unknown) => {key: K, value: V},
  ): Map<K, V> | null {
    try {
      return MapUtils.getMap(map, key, mapFunc);
    }
    catch{
      return null;
    }
  }

  static set(map: SerializedData, key: string, value: unknown): void {
    map[key] = value;
  }

  static setOptional(map: SerializedData, key: string, value: unknown): void {
    if(isNotNullOrUndefined(value)) {
      map[key] = value;
    }
  }

  static setIfNullOrUndefined(map: SerializedData, key: string, value: unknown): void {
    const currentValue = map[key];
    if(isNullOrUndefined(currentValue)) {
      map[key] = value;
    }
  }

  static getList<T>(map: SerializedData, key: string, mapFunc: (data: unknown) => T, defValue?: T[]): T[] {
    const value = map[key];

    if(isNotNullOrUndefined(value)) {
      if(value instanceof Array) {
        return value.map((e) => mapFunc(e));
      }
    }

    if(isNotNullOrUndefined(defValue)) {
      return defValue;
    }

    return [];
  }

  static getListOrNull<T>(map: SerializedData, key: string, mapFunc: (data: unknown) => T): T[] | null {
    const value = map[key];

    if(isNotNullOrUndefined(value)) {
      if(value instanceof Array) {
        return value.map((e) => mapFunc(e));
      }
    }

    return null;
  }

  static getListOrNullOrUndefined<T>(map: SerializedData, key: string, mapFunc: (data: unknown) => T): T[] | null | undefined {
    const value = map[key];

    if(value === undefined) return undefined;
    if(value === null) return null;

    if(value instanceof Array) {
      return value.map((e) => mapFunc(e));
    }

    return null;
  }

  static getRawList(map: SerializedData, key: string, defValue?: unknown[]): unknown[] {
    const value = map[key];
    if(value instanceof Array) {
      return value;
    }

    if(isNotNullOrUndefined(defValue)) {
      return defValue;
    }

    throw new SerializationException(`Missing field with key: ${key} in data map: ${JSON.stringify(map)}`);
  }

  static getVersionRange(map: SerializedData): VersionRange {
    return VersionRange.mustDecode(MapUtils.get(map, '$schema_version'));
  }

  static getVersionRangeOrNull(map: SerializedData): VersionRange | null {
    return VersionRange.decode(MapUtils.get(map, '$schema_version'));
  }
}
